import * as firebaseApp from "firebase/app";
import type { Auth } from "firebase/auth";

// Workaround for "Module has no exported member" errors in some environments
const { initializeApp, getApps, getApp } = firebaseApp as typeof import("firebase/app");

// --- Configuration ---
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY_HERE",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Check if config is still default/placeholder
export const isConfigured = firebaseConfig.apiKey !== "YOUR_API_KEY_HERE" && !!firebaseConfig.apiKey;

let app: firebaseApp.FirebaseApp;
let auth: Auth;

if (isConfigured) {
    try {
        // Initialize Firebase
        // Use getApps() to prevent double-initialization in development/HMR
        app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    } catch (error) {
        console.error("Firebase Core initialization error:", error);
    }
} else {
    console.warn("⚠️ Firebase Core not configured.");
}

// Lazy-load auth using dynamic import to enable code splitting
let authPromise: Promise<Auth | undefined> | null = null;
export const getAuthInstance = async (): Promise<Auth | undefined> => {
    if (authPromise) return authPromise;

    authPromise = (async () => {
        if (!isConfigured || !app) return undefined;
        try {
            const { getAuth } = await import("firebase/auth");
            auth = getAuth(app);
            return auth;
        } catch (error) {
            console.error("Failed to initialize auth:", error);
            return undefined;
        }
    })();

    return authPromise;
};

export { app };
